import { useState } from "react";
import Avatar from "@mui/material/Avatar";
import AvatarGroup from "@mui/material/AvatarGroup";
import Badge from "@mui/material/Badge";
import IconButton from "@mui/material/IconButton";
import ListItem from "@mui/material/ListItem";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Tooltip from "@mui/material/Tooltip";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { dateTimeFormat } from "../../../util/DateUtil";
import { ADMIN, COORDENADOR, PROFESSOR } from "../../Usuario/Perfil";

const getFoto = (usuario) => (usuario.fileName && usuario.fotoUrl) || usuario.foto || undefined;

const getIniciais = (nome = "") =>
  nome
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((parte) => parte[0].toUpperCase())
    .join("");

const UsuarioAvatar = ({ usuario }) => (
  <Avatar src={getFoto(usuario)} alt={usuario.nome}>
    {getIniciais(usuario.nome)}
  </Avatar>
);

export const SalaAvatar = ({ sala, usuarios }) => {
  if (sala.isGroup) {
    return (
      <Avatar sx={{ bgcolor: "secondary.main" }}>
        <GroupsOutlinedIcon />
      </Avatar>
    );
  }
  if (usuarios.length <= 1) {
    return usuarios[0] ? <UsuarioAvatar usuario={usuarios[0]} /> : <Avatar />;
  }
  return (
    <AvatarGroup max={2} sx={{ "& .MuiAvatar-root": { width: 32, height: 32, fontSize: 14 } }}>
      {usuarios.map((usuario) => (
        <UsuarioAvatar key={usuario.id ?? usuario.nome} usuario={usuario} />
      ))}
    </AvatarGroup>
  );
};

/**
 * Item da lista de salas de mensagem.
 * `usuarios` são os participantes exibidos no avatar (ignorado em grupos).
 */
const SalaListItem = ({ sala, usuarios = [], title, quantNaoLidas = 0, onOpen }) => {
  const user = useSelector((state) => state.usuario);
  const [anchorEl, setAnchorEl] = useState(null);

  const podeGerenciarGrupo =
    sala.isGroup && [ADMIN, COORDENADOR, PROFESSOR].includes(user?.perfil);
  const naoLida = quantNaoLidas > 0;

  return (
    <ListItem
      disablePadding
      divider
      secondaryAction={
        podeGerenciarGrupo && (
          <>
            <Tooltip title="Opções do grupo">
              <IconButton edge="end" onClick={(event) => setAnchorEl(event.currentTarget)}>
                <MoreVertIcon />
              </IconButton>
            </Tooltip>
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
              <MenuItem component={Link} to="/group-form" state={{ sala }}>
                Atualizar Grupo
              </MenuItem>
              <MenuItem component={Link} to="/group-list-participants" state={{ sala }}>
                Lista Participantes
              </MenuItem>
            </Menu>
          </>
        )
      }
    >
      <ListItemButton onClick={onOpen} sx={{ py: 1.5, pr: podeGerenciarGrupo ? 8 : 2 }}>
        <ListItemAvatar sx={{ minWidth: 72 }}>
          <Badge
            color="success"
            badgeContent={quantNaoLidas > 9 ? "9+" : quantNaoLidas}
            invisible={!naoLida}
            overlap="circular"
          >
            <SalaAvatar sala={sala} usuarios={usuarios} />
          </Badge>
        </ListItemAvatar>
        <ListItemText
          primary={title}
          secondary={
            sala.dataHoraUtlimaMensagem
              ? `Última mensagem: ${dateTimeFormat(sala.dataHoraUtlimaMensagem)}`
              : "Sem mensagens"
          }
          slotProps={{
            primary: { fontWeight: naoLida ? 700 : 500, noWrap: true },
            secondary: { color: naoLida ? "text.primary" : "text.secondary" },
          }}
        />
      </ListItemButton>
    </ListItem>
  );
};

export default SalaListItem;
